import { useNavigate } from 'react-router-dom';
import { Bell, Map } from 'lucide-react';
import { getTimeAgo } from '../../../utils/trackingHelpers';

const VoucherGuestsList = ({ voucher, guests, loading, onPing }) => {
  const navigate = useNavigate();

  const sortedGuests = Array.isArray(guests)
    ? [...guests].sort((a, b) => {
        const aActive = Boolean(a.is_tracking);
        const bActive = Boolean(b.is_tracking);
        if (aActive && !bActive) return -1;
        if (!aActive && bActive) return 1;
        return 0;
      })
    : [];

  return (
    <div className="bg-slate-50/50 rounded-2xl p-4 border border-slate-100 animate-fade-in">
      <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-200/60">
        <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          Logged-in Guests & Travelers
        </h4>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPing(voucher.bookingID, 'all');
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-500 bg-amber-50 rounded-lg hover:bg-amber-100 transition-all border border-amber-100 cursor-pointer"
        >
          <Bell size={12} />
          Notify All Guests
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-6">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : sortedGuests?.length > 0 ? (
        <div className="divide-y divide-slate-100 bg-white rounded-xl border border-slate-100">
          {sortedGuests.map((guest, gIdx) => {
            const isLive = Boolean(guest.is_tracking || guest.is_active);
            const hasCoords = Boolean(guest.lat && guest.lng && guest.lat !== 0 && guest.lng !== 0);
            const isUnavailable = guest.status === 'unavailable' || guest.location_unavailable;
            const timestamp = guest.last_updated || guest.last_seen;

            return (
              <div key={gIdx} className="flex items-center justify-between p-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600 text-xs border border-slate-200/50">
                    {guest.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 leading-snug">{guest.name}</p>
                    {isLive ? (
                      <div className="flex flex-col mt-0.5">
                        <div className="flex items-center gap-1.5 text-xs text-primary font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                          <span>Sharing location</span>
                        </div>
                        <span className="text-[11px] text-slate-400 pl-3">
                          Updated {getTimeAgo(timestamp)}
                        </span>
                      </div>
                    ) : isUnavailable ? (
                      <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium mt-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                        <span>Location unavailable</span>
                      </div>
                    ) : hasCoords ? (
                      <div className="flex flex-col mt-0.5">
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                          <span>Not sharing</span>
                        </div>
                        <span className="text-[11px] text-slate-400 pl-3">
                          Last seen {getTimeAgo(timestamp)}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col mt-0.5">
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                          <span>Not sharing</span>
                        </div>
                        <span className="text-[11px] text-slate-400 pl-3">
                          No location history
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPing(voucher.bookingID, guest.name);
                    }}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-all cursor-pointer"
                  >
                    <Bell size={12} />
                    Ping
                  </button>

                  {hasCoords && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/tracking/map?voucherId=${voucher.bookingID}&guestName=${encodeURIComponent(guest.name)}`
                        );
                      }}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${
                        isLive
                          ? 'text-primary bg-primary/5 hover:bg-primary/10 border-primary/20'
                          : 'text-slate-700 bg-white hover:bg-slate-50 border-slate-200'
                      }`}
                    >
                      <Map size={12} />
                      {isLive ? 'Track' : 'View'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-slate-400 italic text-center py-4">No guest devices logged in yet.</p>
      )}
    </div>
  );
};

export default VoucherGuestsList;
